import { createTheme } from "@mui/material";
const mixColor = (color, target, weight) => {
    const channels = [1, 3, 5].map((offset) => Number.parseInt(color.slice(offset, offset + 2), 16));
    const destination = target === "#000000" ? 0 : 255;
    return `#${channels
        .map((channel) => Math.round(channel + (destination - channel) * weight)
        .toString(16)
        .padStart(2, "0"))
        .join("")}`;
};
export const projectResolvedTheme = (resolvedTheme) => {
    const values = resolvedTheme.values;
    const brand = values["brand.primary"];
    const dark = resolvedTheme.mode === "dark";
    const densityFactor = {
        compact: 0.8,
        standard: 1,
        comfortable: 1.2,
    }[values["density.mode"]];
    const spacingScale = values["spacing.scale"] * densityFactor;
    const spacing = 8 * spacingScale;
    const controlHeight = Math.round(values["control.height"] * densityFactor);
    const gridRowHeight = Math.round(values["grid.rowHeight"] * densityFactor);
    const reducedMotion = values["motion.reduced"] || values["accessibility.reducedMotion"];
    const highContrast = values["accessibility.highContrast"] ||
        values["appearance.contrastMode"] === "high";
    return {
        muiTheme: createTheme({
            palette: {
                mode: resolvedTheme.mode,
                primary: { main: brand },
                secondary: { main: values["brand.secondary"] },
                success: { main: values["color.status.success"] },
                warning: { main: values["color.status.warning"] },
                error: { main: values["color.status.error"] },
                info: { main: values["color.status.information"] },
                background: {
                    default: values["color.page.background"],
                    paper: values["color.surface.default"],
                },
                text: {
                    primary: values["color.text.primary"],
                    secondary: values["color.text.secondary"],
                    disabled: values["color.text.disabled"],
                },
                divider: values["color.border.subtle"],
            },
            shape: { borderRadius: values["shape.radius.medium"] },
            spacing,
            typography: {
                fontFamily: values["typography.family.primary"],
                fontSize: 14 * values["typography.scale"],
                fontWeightRegular: values["typography.weight.regular"],
                fontWeightMedium: values["typography.weight.medium"],
                fontWeightBold: values["typography.weight.bold"],
            },
            transitions: {
                ...(reducedMotion
                    ? {
                        duration: {
                            shortest: 0,
                            shorter: 0,
                            short: 0,
                            standard: 0,
                            complex: 0,
                            enteringScreen: 0,
                            leavingScreen: 0,
                        },
                    }
                    : {}),
            },
            components: {
                MuiButtonBase: {
                    defaultProps: { disableRipple: reducedMotion },
                    styleOverrides: {
                        root: {
                            minHeight: controlHeight,
                            "&:focus-visible": {
                                outline: `${highContrast ? 3 : 2}px solid ${values["color.border.focus"]}`,
                                outlineOffset: 2,
                            },
                        },
                    },
                },
            },
        }),
        cssVariables: {
            "--iaf-color-brand-50": dark
                ? mixColor(brand, "#000000", 0.55)
                : mixColor(brand, "#ffffff", 0.9),
            "--iaf-color-brand-100": dark
                ? mixColor(brand, "#000000", 0.35)
                : mixColor(brand, "#ffffff", 0.78),
            "--iaf-color-brand-500": brand,
            "--iaf-color-brand-600": mixColor(brand, "#000000", 0.16),
            "--iaf-color-brand-700": dark
                ? mixColor(brand, "#ffffff", 0.45)
                : mixColor(brand, "#000000", 0.32),
            "--iaf-color-neutral-0": values["color.surface.default"],
            "--iaf-color-neutral-50": values["color.page.background"],
            "--iaf-color-neutral-100": values["color.border.subtle"],
            "--iaf-color-neutral-600": values["color.text.muted"],
            "--iaf-color-neutral-900": values["color.text.primary"],
            "--iaf-color-info": values["color.status.information"],
            "--iaf-color-success": values["color.status.success"],
            "--iaf-color-warning": values["color.status.warning"],
            "--iaf-color-danger": values["color.status.error"],
            "--iaf-radius-small": `${values["shape.radius.small"] / 16}rem`,
            "--iaf-radius-medium": `${values["shape.radius.medium"] / 16}rem`,
            "--iaf-radius-large": `${values["shape.radius.large"] / 16}rem`,
            "--iaf-visualization-hover": values["motion.enabled"] && !reducedMotion
                ? values["visualization.hoverEffect"] : "none",
            "--iaf-visualization-style": values["visualization.cardStyle"],
            "--iaf-visualization-transition": `${180 * values["motion.durationScale"]}ms`,
            "--iaf-grid-row-height": `${gridRowHeight}px`,
            "--iaf-control-height": `${controlHeight}px`,
            "--iaf-space-1": `${0.25 * spacingScale}rem`,
            "--iaf-space-2": `${0.5 * spacingScale}rem`,
            "--iaf-space-3": `${0.75 * spacingScale}rem`,
            "--iaf-space-4": `${spacingScale}rem`,
            "--iaf-space-6": `${1.5 * spacingScale}rem`,
            "--iaf-space-8": `${2 * spacingScale}rem`,
            colorScheme: resolvedTheme.mode,
            fontFamily: values["typography.family.primary"],
            fontSize: `${values["typography.scale"]}rem`,
            lineHeight: values["typography.lineHeight"],
            minHeight: "100vh",
        },
    };
};
//# sourceMappingURL=theme-adapter.js.map